import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocalData } from '../../../hooks/useLocalData';
import { Button, Col, Form, Row, Select } from 'antd';
import { useForm, useWatch } from 'antd/es/form/Form';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import FormModelComponent from '../../../components/model/FormModelComponent';
import { Plus, XSquare } from 'react-bootstrap-icons';
import ConfirmModelComponent from '../../../components/model/ConfirmationModelComponent';
import { useTranslation } from 'react-i18next';
import { referenceDataSyncService } from '../../../services/referenceDataSyncService';
import PrimarySquareBtn from '../../../components/buttons/PrimarySquareBtn';
import { normalize } from '../../../utils/normalizeWords';
import { useIsOnlineContext } from '../../../contexts/IsOnlineContext';
import AddMainProductForm from '../../../components/mainProduct/AddMainProduct';
import { mainProductService } from '../../../api/services/mainProductService';
import MainProductList from '../../../components/mainProduct/MainProductList';
import useDebounce from '../../../hooks/useDebounce';
import { REGEX_ALL_ALLOWED_SAFE_FILTER } from '../../../constants/regex';
import { FormField } from '../../../components/form/FormBasic';
import { ActiveFilterEnum } from '../../../enums/ActiveFilterEnum';
import { FormFieldTypeEnum } from '../../../enums/FormFieldTypeEnum';
import {
  maxLengthRule,
  minLengthRule,
  noSpacesOnlyRule,
  regexRule,
} from '../../../helpers/formHelper';

const MainProductView: React.FC = () => {
  const [addMainProductForm] = useForm();
  const [filterForm] = useForm();

  const { t } = useTranslation(['mainProduct', 'form', 'common']);
  const { successNotification, errorNotification } = useNotificationContext();
  const { isOnline } = useIsOnlineContext();

  // Modal and confirmation states
  const [isMainProductModalOpen, setIsMainProductModalOpen] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState<boolean>(false);
  const [isRestoreConfirmationOpen, setIsRestoreConfirmationOpen] = useState<boolean>(false);
  const [categoryOptions, setCategoryOptions] = useState<any[]>([]);
  const [isEditDataLoading, setIsEditDataLoading] = useState<boolean>(false);
  const [isConfirmClicked, setIsConfirmClicked] = useState<boolean>(false);

  // Filter states
  const [selectedProductCategories, setSelectedProductCategories] = useState<string[]>([]);
  const [productCategories, setProductCategories] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState<ActiveFilterEnum>(ActiveFilterEnum.ACTIVE);
  const [isSearchValid, setIsSearchValid] = useState<boolean>(true);
  const [isLoadingFilterData, setIsLoadingFilterData] = useState<boolean>(false);

  // Debounce states
  const rawSearchName = useWatch('searchName', filterForm) || '';
  const debouncedSearchName = useDebounce(rawSearchName, 500);

  const enableClearFiltersBtn = selectedProductCategories?.length > 0 || rawSearchName?.trim() !== '';

  // Table states
  const [isLoadingProducts, setIsLoadingProducts] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalProducts, setTotalProducts] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);

  // Validate the product name search field
  useEffect(() => {
    if (!rawSearchName || rawSearchName.trim() === '') {
      setIsSearchValid(true);
      return;
    }

    const trimmedLen = rawSearchName.replace(/\s/g, '').length;
    const passesRegex = REGEX_ALL_ALLOWED_SAFE_FILTER.test(String(rawSearchName));
    const passesLength = trimmedLen >= 1 && trimmedLen <= 100;

    setIsSearchValid(passesRegex && passesLength);
  }, [rawSearchName]);

  const memoizedFilters = useMemo(() => {
    if (selectedProductCategories?.length === 0) return undefined;
    return { productCategoryIds: selectedProductCategories };
  }, [selectedProductCategories]);

  const cleanQuerySearch = useMemo(() => {
    return isSearchValid ? debouncedSearchName.trim() : '';
  }, [debouncedSearchName, isSearchValid]);

  //IndexedDB data
  const {
    data: mainProducts,
    total: totalIndexedDbItems,
    loading: isIndexedDbLoading,
    refresh: refreshLocalIndexedDB,
  } = useLocalData({
    entity: 'mainProduct',
    autoSync: true,
    page: currentPage,
    limit: pageSize,
    sortBy: 'createTime',
    sortOrder: 'desc',
    search: cleanQuerySearch || undefined,
    filters: memoizedFilters,
    fieldMapping: {
      productCategoryIds: 'productCategory.id',
    },
  });

  const [mainProductData, setMainProductData] = useState<any[]>(mainProducts);

  const fetchFilterData = useCallback(async () => {
    setIsLoadingFilterData(true);
    try {
      const categoriesData = await referenceDataSyncService.getProductCategories();
      setProductCategories(
        categoriesData.sort((a, b) => a.mainCategory.localeCompare(b.mainCategory))
      );
    } catch (error) {
      errorNotification(t('filterOptionsLoadFail'));
    } finally {
      setIsLoadingFilterData(false);
    }
  }, []);

  const getAllProductList = useCallback(
    async (
      page: number = currentPage,
      searchName: string = cleanQuerySearch,
      categoryIds: string[] = selectedProductCategories,
      customPageSize?: number,
      status: ActiveFilterEnum = statusFilter
    ) => {
      if (!isOnline) return;

      const pageLimit = customPageSize !== undefined ? customPageSize : pageSize;

      if (!isSearchValid) {
        setMainProductData([]);
        setTotalProducts(0);
        return;
      }

      setIsLoadingProducts(true);
      try {
        const filterParams: any = {
          page,
          limit: pageLimit,
          status,
        };

        if (categoryIds && categoryIds.length > 0) {
          filterParams.productCategoryIds = categoryIds;
        }

        if (searchName !== '') {
          filterParams.search = searchName;
        }

        const response = await mainProductService.getMainProductList(filterParams);

        if (response?.statusText === 'SUCCESS' || response?.status === 200) {
          const products = response?.data?.data?.data || response?.data || [];
          const total = response?.data?.data?.total || 0;
          setMainProductData(products);
          setTotalProducts(total);
        }
      } catch (error) {
        errorNotification(t('productFetchError'));
      } finally {
        setIsLoadingProducts(false);
      }
    },
    [pageSize, isOnline, isSearchValid, statusFilter, t]
  );

  useEffect(() => {
    fetchFilterData();
  }, [fetchFilterData]);

  const lastQueryRef = useRef({ search: cleanQuerySearch, categories: selectedProductCategories });

  useEffect(() => {
    if (!isOnline) return;

    const queryChanged =
      lastQueryRef.current.search !== cleanQuerySearch ||
      lastQueryRef.current.categories !== selectedProductCategories;

    if (queryChanged) {
      lastQueryRef.current = { search: cleanQuerySearch, categories: selectedProductCategories };
      if (currentPage !== 1) {
        setCurrentPage(1);
        return;
      }
    }

    getAllProductList(currentPage, cleanQuerySearch, selectedProductCategories, pageSize, statusFilter);
  }, [
    currentPage,
    pageSize,
    cleanQuerySearch,
    selectedProductCategories,
    statusFilter,
    isOnline,
    getAllProductList,
  ]);

  const clearAllFilters = () => {
    filterForm.resetFields();
    setSelectedProductCategories([]);
    setStatusFilter(ActiveFilterEnum.ACTIVE);
  };

  // Reset status filter to 'active' when user goes offline
  useEffect(() => {
    if (!isOnline) {
      setStatusFilter(ActiveFilterEnum.ACTIVE);
      filterForm.setFieldValue('status', ActiveFilterEnum.ACTIVE);
    }
  }, [isOnline, filterForm]);

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }
  };

  const handleCancel = () => {
    addMainProductForm.resetFields();
    setIsMainProductModalOpen(false);
  };

  const getConfirmation = async () => {
    await addMainProductForm.validateFields();
    setIsConfirmationOpen(true);
  };

  const handleCreate = async () => {
    setIsConfirmClicked(true);
    const productFormData = await addMainProductForm.getFieldsValue();

    if (Array.isArray(productFormData.brands)) {
      productFormData.brands = productFormData.brands.filter((b: string) => b && b.trim() !== '');
    }

    setIsConfirmationOpen(false);

    if (!isOnline) {
      errorNotification(isEdit ? t('offlineEditInfoTitle') : t('offlineAddInfoTitle'));
      setIsConfirmClicked(false);
      return;
    }

    setIsMainProductModalOpen(false);
    setIsLoadingProducts(true);

    const body: any = {
      name: productFormData?.name?.trim(),
      presentations: productFormData?.presentations,
      brands: productFormData?.brands,
      unit: productFormData?.unit,
      productCategoryId: productFormData?.productCategoryId
        ? parseInt(productFormData.productCategoryId)
        : null,
      isActive: true,
    };

    try {
      if (isEdit) {
        const result = await mainProductService.updateMainProduct(selectedProductId, body);
        if (result) successNotification(t('productUpdateSuccess'));
      } else {
        const result = await mainProductService.createMainProduct(body);
        if (result) successNotification(t('productCreateSuccess'));
      }

      addMainProductForm.resetFields();

      if (isOnline) {
        await getAllProductList(currentPage, cleanQuerySearch, selectedProductCategories, pageSize);
      } else {
        await refreshLocalIndexedDB();
      }
    } catch (error: any) {
      errorNotification(
        error?.message || (isEdit ? t('productUpdateFail') : t('productCreateFail'))
      );
    } finally {
      setIsLoadingProducts(false);
      setIsConfirmClicked(false);
    }
  };

  const getDeleteConfirmation = (id: string) => {
    setIsDeleteConfirmationOpen(true);
    setSelectedProductId(id);
  };

  const getRestoreConfirmation = (id: string) => {
    setIsRestoreConfirmationOpen(true);
    setSelectedProductId(id);
  };

  const handleDelete = async () => {
    try {
      setIsConfirmClicked(true);
      setIsDeleteConfirmationOpen(false);
      setIsLoadingProducts(true);

      await mainProductService.deleteMainProduct(selectedProductId);
      successNotification(t('productDeleteSuccess'));

      if (isOnline) {
        await getAllProductList(currentPage, cleanQuerySearch, selectedProductCategories, pageSize, statusFilter);
      } else {
        await refreshLocalIndexedDB();
      }
    } catch (error: any) {
      errorNotification(error?.message || t('productDeleteFail'));
    } finally {
      setIsConfirmClicked(false);
      setIsLoadingProducts(false);
    }
  };

  const handleRestore = async () => {
    try {
      setIsConfirmClicked(true);
      setIsRestoreConfirmationOpen(false);
      setIsLoadingProducts(true);

      await mainProductService.restoreMainProduct(selectedProductId);
      successNotification(t('productRestoreSuccess'));

      await getAllProductList(currentPage, cleanQuerySearch, selectedProductCategories, pageSize, statusFilter);
    } catch (error: any) {
      errorNotification(error?.message || t('productRestoreFail'));
    } finally {
      setIsConfirmClicked(false);
      setIsLoadingProducts(false);
    }
  };

  const handleEdit = async (data: any) => {
    try {
      setSelectedProductId(data?.id);
      setIsMainProductModalOpen(true);
      setIsEditDataLoading(true);

      if (!data?.id) {
        errorNotification('Product not found');
        return;
      }
      const categoryIdStr = data?.productCategory?.id?.toString();

      const formValues: any = {
        ...data,
        presentations: data?.presentations?.map((p: any) => p.name) || [''],
        brands: data?.brands?.map((b: any) => b.name) || [],
        productCategory: categoryIdStr,
        productCategoryId: data?.productCategory?.id,
      };

      addMainProductForm.setFieldsValue(formValues);
      setIsEdit(true);
    } catch (error) {
      errorNotification(t('productLoadFail'));
    } finally {
      setIsEditDataLoading(false);
    }
  };

  const handleNewProductButton = () => {
    setIsEdit(false);
    setIsMainProductModalOpen(true);
    setCategoryOptions([]);
    addMainProductForm.resetFields();
  };

  return (
    <>
      <ConfirmModelComponent
        isOpen={isConfirmationOpen}
        onClickedCancel={() => setIsConfirmationOpen(false)}
        header={isEdit ? t('confirmUpdateTitle') : t('confirmCreateTitle')}
        content={<p>{isEdit ? t('confirmUpdateMessage') : t('confirmCreateMessage')}</p>}
        footerAction={{
          confimation: { label: t('confirm'), onClick: handleCreate, isDisabled: isConfirmClicked },
          cancel: { label: t('cancel'), onClick: () => setIsConfirmationOpen(false) },
        }}
        styleClasses="z-100"
      />

      <ConfirmModelComponent
        isOpen={isDeleteConfirmationOpen}
        onClickedCancel={() => setIsDeleteConfirmationOpen(false)}
        header={t('confirmDeleteTitle')}
        content={<p>{t('confirmDeleteMessage')}</p>}
        footerAction={{
          confimation: { label: t('confirm'), onClick: handleDelete, isDisabled: isConfirmClicked },
          cancel: { label: t('cancel'), onClick: () => setIsDeleteConfirmationOpen(false) },
        }}
        styleClasses="z-100"
      />

      <ConfirmModelComponent
        isOpen={isRestoreConfirmationOpen}
        onClickedCancel={() => setIsRestoreConfirmationOpen(false)}
        header={t('confirmRestoreTitle')}
        content={<p>{t('confirmRestoreMessage')}</p>}
        footerAction={{
          confimation: { label: t('confirm'), onClick: handleRestore, isDisabled: isConfirmClicked },
          cancel: { label: t('cancel'), onClick: () => setIsRestoreConfirmationOpen(false) },
        }}
        styleClasses="z-100"
      />

      <FormModelComponent
        isOpen={isMainProductModalOpen}
        header={isEdit ? t('updateProductTitle') : t('createProductTitle')}
        subheader={isEdit ? t('updateProductSubTitle') : t('createProductSubTitle')}
        content={
          <AddMainProductForm
            form={addMainProductForm}
            handleCancel={handleCancel}
            getConfirmation={getConfirmation}
            isEdit={isEdit}
            initialCategoryOptions={categoryOptions}
            isEditDataLoading={isEditDataLoading}
          />
        }
        onClickedCancel={handleCancel}
        footerAction={{
          confimation: {
            label: `${isEdit ? t('updateProduct') : t('createProduct')}`,
            onClick: getConfirmation,
          },
          cancel: { label: t('cancel'), onClick: handleCancel },
        }}
        closeIcon={<XSquare className="text-primary-font-red" size={20} />}
        contentStyles="mb-4 px-2 max-h-[60vh] md:max-h-[70vh] overflow-y-auto space-y-2"
      />

      <div className="mt-4 flex w-full flex-col justify-start gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-xl font-medium text-headings-font-color">
            {t('mainProductMainHeading')}
          </h2>
          <p className="text-base font-medium text-font-color">{t('mainProductSubHeading')}</p>
        </div>

        <div className="flex justify-end" title={!isOnline ? t('offlineAddInfoTitle') : undefined}>
          <PrimarySquareBtn handleOnClick={handleNewProductButton} disabled={!isOnline}>
            <div className="flex">
              <Plus className="h-6 w-6 text-white" />
              <p className="pt-1">{t('newMainProduct')}</p>
            </div>
          </PrimarySquareBtn>
        </div>
      </div>

      <div className="my-6">
        <section className="mb-8 w-full">
          <Form
            form={filterForm}
            layout="vertical"
            className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">{t('filters')}</h3>
              <Button
                type="link"
                onClick={clearAllFilters}
                disabled={!enableClearFiltersBtn}
                className="hover:text-primary-dark text-sm text-primary"
              >
                {t('clearFilters')}
              </Button>
            </div>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8} lg={6}>
                <FormField
                  name="searchName"
                  label={t('mainProduct:labels.productName')}
                  placeholder={t('form:searchByPlaceholder', {
                    field: t('mainProduct:placeholders.searchProduct'),
                  })}
                  rules={[
                    noSpacesOnlyRule(t, t('mainProduct:labels.productName')),
                    maxLengthRule(t, t('mainProduct:labels.productName'), 100),
                    regexRule(t, REGEX_ALL_ALLOWED_SAFE_FILTER),
                  ]}
                  disabled={false}
                  type={FormFieldTypeEnum.INPUT}
                  allowLeadingTrailingSpaces={true}
                  useCustomRules={true}
                  allowClear={true}
                />
              </Col>

              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item label={t('labels.productCategories')} name="categories">
                  <Select
                    className="h-[38px] w-full [&>.ant-select-selector]:!rounded-lg"
                    placeholder={t('form:dropdownPlaceholder', {
                      field: t('placeholders.categories'),
                    })}
                    mode="multiple"
                    maxTagCount={1}
                    maxTagTextLength={window.matchMedia('(min-width: 1600px)').matches ? 15 : 10}
                    allowClear
                    value={selectedProductCategories}
                    onChange={setSelectedProductCategories}
                    loading={isLoadingFilterData}
                    options={productCategories.map((category: any) => ({
                      label: `${category.mainCategory}${category.category ? ` > ${category.category}` : ''}`,
                      value: category.id.toString(),
                    }))}
                    showSearch
                    filterOption={(input, option) =>
                      normalize(option?.label ?? '')
                        .toLowerCase()
                        .includes(normalize(input.toLowerCase()))
                    }
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item label={t('labels.status')} name="status">
                  <div title={!isOnline ? t('offlineStatusInfoTitle') : undefined}>
                    <Select
                      className="h-[38px] w-full [&>.ant-select-selector]:!rounded-lg"
                      value={statusFilter}
                      disabled={!isOnline}
                      onChange={(val: ActiveFilterEnum) => {
                        setStatusFilter(val);
                        setCurrentPage(1);
                      }}
                      options={Object.values(ActiveFilterEnum).map((status) => ({
                        label: t(`common:statusFilter.${status}`),
                        value: status,
                      }))}
                    />
                  </div>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </section>

        <div className="my-6 w-full">
          <MainProductList
            products={isOnline ? mainProductData : mainProducts}
            getDeleteConfirmation={getDeleteConfirmation}
            getRestoreConfirmation={getRestoreConfirmation}
            handleEdit={handleEdit}
            totalProducts={isOnline ? totalProducts : totalIndexedDbItems}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            loading={isOnline ? isLoadingProducts : isIndexedDbLoading}
            statusFilter={statusFilter}
          />
        </div>
      </div>
    </>
  );
};

export default MainProductView;
